import apiClient from '../api/httpClient';
import endpoints from '../api/endpoints';
import devicesApi from '../api/devicesApi';
import filesApi from '../api/filesApi';
import otaApi from '../api/otaApi';
import sensorsApi from '../api/sensorsApi';
import commonTranslations from '../languages/libCommon.json';
import { getErrorMessage } from '../utils/errorUtils';

const options = { headers: { "Content-Type": "application/json" } };

function getLang() {
    return String(localStorage.getItem('appLang') || 'en').toLowerCase();
}

function c() {
    return commonTranslations[getLang()]?.actions || commonTranslations.en?.actions || {};
}

function formatMessage(template, values = {}) {
    return Object.entries(values).reduce(
        (message, [key, value]) => message.replaceAll(`{${key}}`, value),
        template || ''
    );
}

/**
 * AppDialog "alert" adapter:
 * - Pass your AppDialog promise-based function (like loginAction's `alert`) as the last param.
 * - If not provided, it falls back to window.confirm / window.alert.
 *
 * Expected signature:
 *   const result = await alert({ type:"confirm"| "alert", variant, icon, title, message, confirmText, cancelText, showCancel })
 *   - For confirm: return boolean (true/false). If you return nothing, it will be treated as "true".
 */
async function appConfirm(alert, opts) {
    const t = c();
    const msg = opts?.message || t.confirmDeleteSingle || 'Are you sure?';

    if (typeof alert === 'function') {
        try {
            const r = await alert({
                type: "confirm",
                variant: opts?.variant || "danger",
                icon: (opts?.icon ?? ((opts?.variant === "danger" || opts?.variant === "error") ? "delete" : "alert")),
                title: opts?.title || t.confirmTitle || "Confirm",
                message: msg,
                confirmText: opts?.confirmText || t.confirm || "Ok",
                cancelText: opts?.cancelText || t.cancel || "Cancel",
                showCancel: true,
            });
            return typeof r === 'boolean' ? r : true;
        } catch (_) {
            // fallback below
        }
    }

    return window.confirm(msg);
}

async function appAlert(alert, opts) {
    const t = c();
    const msg = opts?.message || '';

    if (typeof alert === 'function') {
        try {
            await alert({
                type: "alert",
                variant: opts?.variant || "info",
                icon: (opts?.icon ?? "alert"),
                title: opts?.title || "",
                message: msg,
                confirmText: opts?.confirmText || t.done || "Ok",
            });
            return;
        } catch (_) {
            // fallback below
        }
    }

    window.alert(msg);
}

async function appPrompt(alert, opts) {
    const t = c();
    const msg = opts?.message || '';

    if (typeof alert === 'function') {
        try {
            const value = await alert({
                type: "prompt",
                variant: opts?.variant || "danger",
                icon: opts?.icon || "delete",
                title: opts?.title || t.confirmTitle || "Confirm",
                message: msg,
                inputLabel: opts?.inputLabel || "",
                inputPlaceholder: opts?.inputPlaceholder || "",
                inputType: opts?.inputType || "text",
                inputValue: opts?.inputValue || "",
                confirmText: opts?.confirmText || t.confirm || "Confirm",
                cancelText: opts?.cancelText || t.cancel || "Cancel",
                showCancel: true,
            });
            return typeof value === 'string' ? value : null;
        } catch (_) {
            // fallback below
        }
    }

    return window.prompt(msg);
}

async function getSuperPasswordForSubscriberDelete(alert, multi = false) {
    const t = c();
    const password = await appPrompt(alert, {
        title: multi
            ? (t.deleteSubscribersTitle || "Delete Subscribers")
            : (t.deleteSubscriberTitle || "Delete Subscriber"),
        variant: "danger",
        icon: "delete",
        message: t.confirmDeleteSubscriberWithPassword || "Enter your Super password to confirm subscriber deletion.",
        inputLabel: t.superPasswordLabel || "Super password",
        inputPlaceholder: t.superPasswordPlaceholder || "Enter password",
        inputType: "password",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });

    if (password === null) return null;
    const trimmedPassword = String(password).trim();
    if (!trimmedPassword) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: t.superPasswordRequired || "Super password is required.",
        });
        return null;
    }
    return trimmedPassword;
}

export const deleteAssetById = async (assetId, assetName, organization_id, navigateTo, alert) => {
    const t = c();

    const confirmationMessage = (t.confirmDeleteAsset || "Are you sure you want to delete this asset?")
        .replace('{assetName}', assetName || `ID: ${assetId}`)
        .replace('{assetId}', assetId);

    const confirmed = await appConfirm(alert, {
        title: t.deleteAssetTitle || "Delete Asset",
        icon: "delete",
        message: confirmationMessage,
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await apiClient.delete(endpoints.assetCard.delete(assetId), { data: { organization_id } });

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: formatMessage(t.deleteAssetSuccess, { name: assetName || assetId }),
        });

        if (navigateTo) navigateTo();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err, `Failed to delete asset '${assetName || assetId}'.`),
        });
    }
};

export const deleteMultiAssetCards = async (selectedAssetIds, organization_id, refreshFunc, alert) => {
    const t = c();

    if (!selectedAssetIds || selectedAssetIds.length === 0) {
        await appAlert(alert, {
            variant: "info",
            title: t.noSelectionTitle || "No selection",
            message: t.noItemsSelected || "No items selected.",
        });
        return;
    }

    const confirmMessage = (t.confirmDeleteMultiAssets || "Are you sure you want to delete selected items?")
        .replace('{count}', selectedAssetIds.length);

    const confirmed = await appConfirm(alert, {
        title: t.deleteAssetsTitle || "Delete Assets",
        icon: "delete",
        message: confirmMessage,
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        for (const id of selectedAssetIds) {
            await apiClient.delete(endpoints.assetCard.delete(id), { data: { organization_id } });
        }

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: formatMessage(t.deleteAssetsSuccess, { count: selectedAssetIds.length }),
        });

        refreshFunc?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err, `Failed to delete multiple assets.`),
        });
    }
};

export const deleteUser = async (id, refresh, alert) => {
    const t = c();

    const confirmed = await appConfirm(alert, {
        title: t.deleteUserTitle || "Delete User",
        variant: "danger",
        icon: "delete",
        message: t.confirmDeleteSingle || "Are you sure you want to delete this item?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await apiClient.delete(endpoints.user.delete(id));

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteUserSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteMultiUsers = async (ids, refresh, alert) => {
    const t = c();

    if (!ids || ids.length === 0) {
        await appAlert(alert, {
            variant: "info",
            title: t.noSelectionTitle || "No selection",
            message: t.noItemsSelected || "No items selected.",
        });
        return;
    }

    const confirmed = await appConfirm(alert, {
        title: t.deleteUsersTitle || "Delete Users",
        icon: "delete",
        message: t.confirmDeleteMultiple || "Are you sure you want to delete these items?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await apiClient.post(endpoints.user.deleteMany(), ids, options);

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteUsersSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteSubscriber = async (id, refresh, alert) => {
    const t = c();

    const superPassword = await getSuperPasswordForSubscriberDelete(alert, false);
    if (!superPassword) return;

    try {
        await apiClient.delete(endpoints.subscriber.delete(id), {
            data: { super_password: superPassword },
            ...options,
        });

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteSubscriberSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteMultiSubscribers = async (ids, refresh, alert) => {
    const t = c();

    if (!ids || ids.length === 0) {
        await appAlert(alert, {
            variant: "info",
            title: t.noSelectionTitle || "No selection",
            message: t.noItemsSelected || "No items selected.",
        });
        return;
    }

    const superPassword = await getSuperPasswordForSubscriberDelete(alert, true);
    if (!superPassword) return;

    try {
        await apiClient.post(
            endpoints.subscriber.deleteMany(),
            { ids, super_password: superPassword },
            options
        );

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteSubscribersSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteMultiLocations = async (ids, refresh, alert) => {
    const t = c();

    if (!ids || ids.length === 0) {
        await appAlert(alert, {
            variant: "info",
            title: t.noSelectionTitle || "No selection",
            message: t.noItemsSelected || "No items selected.",
        });
        return;
    }

    const confirmed = await appConfirm(alert, {
        title: t.deleteLocationsTitle || "Delete Locations",
        icon: "delete",
        message: t.confirmDeleteMultiple || "Are you sure you want to delete these items?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await apiClient.post(endpoints.location.deleteMany(), JSON.stringify(ids), options);

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deletedLocationsSuccess || "Locations deleted successfully.",
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
        refresh?.();
    }
};

export const suspendUser = async (id, isCurrentUser, updateCurrentUser, refresh, alert) => {
    const t = c();

    try {
        const res = await apiClient.put(
            endpoints.user.update(id),
            { status: 'Suspended' },
            options
        );

        if (res.status === 200 && res.data.status === 'OK') {
            await appAlert(alert, {
                variant: "info",
                title: t.success || "Success",
                message: formatMessage(t.suspendUserSuccess, { id }),
            });

            if (isCurrentUser) updateCurrentUser?.();
            refresh?.();
            return;
        }

        throw new Error("Received an unexpected response from the server.");
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const resetPassword = async (id, alert) => {
    const t = c();

    const confirmed = await appConfirm(alert, {
        title: t.resetPasswordTitle || "Reset Password",
        message: t.confirmResetPassword || "Are you sure you want to reset the password?",
        confirmText: t.confirm || "Confirm",
        cancelText: t.cancel || "Cancel",
        icon: "delete",
        variant: "danger",
    });
    if (!confirmed) return;

    try {
        const res = await apiClient.put(
            endpoints.user.update(id),
            { password: '' },
            options
        );

        if (res.status === 200 && res.data.status === 'OK') {
            await appAlert(alert, {
                variant: "success",
                title: t.success || "Success",
                message: t.passwordResetSuccess || "Password reset. The user will initialize a new password at next login.",
            });
            return;
        }

        throw new Error("Received an unexpected response from the server.");
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteDevice = async (id, refresh, alert) => {
    const t = c();

    const confirmed = await appConfirm(alert, {
        title: t.deleteDeviceTitle || "Delete Device",
        variant: "danger",
        icon: "delete",
        message: t.confirmDeleteSingle || "Are you sure you want to delete this item?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await devicesApi.delete(id);

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteDeviceSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteMultiDevices = async (ids, refresh, alert) => {
    const t = c();

    if (!ids || ids.length === 0) {
        await appAlert(alert, {
            variant: "info",
            title: t.noSelectionTitle || "No selection",
            message: t.noItemsSelected || "No items selected.",
        });
        return;
    }

    const confirmed = await appConfirm(alert, {
        title: t.deleteDevicesTitle || "Delete Devices",
        icon: "delete",
        message: t.confirmDeleteMultiple || "Are you sure you want to delete these items?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await devicesApi.deleteMany(ids);

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteDevicesSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteDeviceData = async (did, alert, refresh) => {
    const t = c();

    if (!did) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: t.missingDeviceId,
        });
        return;
    }

    const confirmationMessage = (t.confirmDeleteDeviceData || 'Are you sure you want to delete device data?')
        .replace('{did}', did);

    const confirmed = await appConfirm(alert, {
        title: t.deleteDeviceDataTitle || "Delete Device Data",
        icon: "delete",
        message: confirmationMessage,
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        const body = { device: did };
        await devicesApi.deleteData(body);

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteDeviceDataSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

/**
 * deviceAction(did, actionType, message?, alert?)
 * - if message exists => confirm first
 * - then call sensors endpoint and show result in dialog
 */
export const deviceAction = async (did, actionType, message, alert) => {
    const t = c();

    if (message) {
        const ok = await appConfirm(alert, {
            title: t.confirmTitle || "Confirm",
            message,
            confirmText: t.confirm || "Ok",
            cancelText: t.cancel || "Cancel",
            icon: "delete",
            variant: "info",
        });
        if (!ok) return;
    }

    try {
        const res = await sensorsApi.action(actionType, did);

        await appAlert(alert, {
            variant: "info",
            title: t.done || "Done",
            message: String(res.data ?? ''),
        });
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: err?.message || t.requestFailed,
        });
    }
};

export const deleteWithIcon = async (id, icon, type, refresh, alert) => {
    const t = c();

    const confirmed = await appConfirm(alert, {
        title: t.deleteTitle || "Delete",
        variant: "danger",
        icon: "delete",
        message: t.confirmDeleteSingle || "Are you sure you want to delete this item?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await apiClient.delete(endpoints.crud.delete(type, id));

        if (icon) {
            try {
                await filesApi.delete(['/icons/' + icon]);
            } catch (iconErr) {
                console.warn('Non-critical error: Failed to delete associated icon.', iconErr);
            }
        }

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteItemSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteMultiWithIcon = async (ids, icons, type, refresh, alert) => {
    const t = c();

    if (!ids || ids.length === 0) {
        await appAlert(alert, {
            variant: "info",
            title: t.noSelectionTitle || "No selection",
            message: t.noItemsSelected || "No items selected.",
        });
        return;
    }

    const confirmed = await appConfirm(alert, {
        title: t.deleteTitle || "Delete",
        icon: "delete",
        message: t.confirmDeleteMultiple || "Are you sure you want to delete these items?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await apiClient.post(endpoints.crud.deleteMany(type), ids, options);

        if (icons && icons.length > 0) {
            try {
                const validIcons = icons.filter(Boolean).map((ic) => `/icons/${ic}`);
                if (validIcons.length > 0) {
                    await filesApi.delete(validIcons, options);
                }
            } catch (iconErr) {
                console.warn('Non-critical error: Failed to delete associated icons.', iconErr);
            }
        }

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteItemsSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteAutomation = async (id, refresh, alert) => {
    const t = c();

    const confirmed = await appConfirm(alert, {
        title: t.deleteAutomationTitle || "Delete Automation",
        variant: "danger",
        icon: "delete",
        message: t.confirmDeleteSingle || "Are you sure you want to delete this item?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await apiClient.delete(endpoints.automation.delete(id), options);

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteAutomationSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteMultiAutomation = async (ids, refresh, alert) => {
    const t = c();

    if (!ids || ids.length === 0) {
        await appAlert(alert, {
            variant: "info",
            title: t.noSelectionTitle || "No selection",
            message: t.noItemsSelected || "No items selected.",
        });
        return;
    }

    const confirmed = await appConfirm(alert, {
        title: t.deleteAutomationTitle || "Delete Automation",
        icon: "delete",
        message: t.confirmDeleteMultiple || "Are you sure you want to delete these items?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await apiClient.post(endpoints.automation.deleteMany(), ids, options);

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteAutomationSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteShipment = async (id, firmware, refresh, alert) => {
    const t = c();

    const confirmed = await appConfirm(alert, {
        title: t.deleteShipmentTitle || "Delete Shipment",
        variant: "danger",
        icon: "delete",
        message: t.confirmDeleteSingle || "Are you sure you want to delete this item?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await otaApi.deleteShipment(id);

        if (firmware) {
            try {
                await otaApi.deleteFiles([firmware], options);
                console.log("Successfully cleaned up associated firmware:", firmware);
            } catch (firmwareErr) {
                console.warn('Non-critical error: Failed to delete associated firmware.', firmwareErr);
            }
        }

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteShipmentSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteMultiShipments = async (ids, firmwares, refresh, alert) => {
    const t = c();

    if (!ids || ids.length === 0) {
        await appAlert(alert, {
            variant: "info",
            title: t.noSelectionTitle || "No selection",
            message: t.noItemsSelected || "No items selected.",
        });
        return;
    }

    const confirmed = await appConfirm(alert, {
        title: t.deleteShipmentsTitle || "Delete Shipments",
        icon: "delete",
        message: t.confirmDeleteMultiple || "Are you sure you want to delete these items?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await otaApi.deleteShipments(ids, options);

        if (firmwares && firmwares.length > 0) {
            try {
                const validFirmwares = firmwares.filter(Boolean);
                if (validFirmwares.length > 0) {
                    await otaApi.deleteFiles(validFirmwares, options);
                    console.log("Successfully cleaned up associated firmwares.");
                }
            } catch (firmwareErr) {
                console.warn('Non-critical error: Failed to delete associated firmwares.', firmwareErr);
            }
        }

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteShipmentsSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const controlShipment = async (id, operation, refresh, alert) => {
    const t = c();

    try {
        const res = await otaApi.controlShipment(id, operation);

        if (res.data && res.data.status === 'OK') {
            await appAlert(alert, {
                variant: "info",
                title: t.success || "Success",
                message: t.shipmentStatusChangedSuccess,
            });
            refresh?.();
            return;
        }

        throw new Error('Received an invalid response from the server.');
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteRole = async (id, refresh, alert) => {
    const t = c();

    const confirmed = await appConfirm(alert, {
        title: t.deleteRoleTitle || "Delete Role",
        variant: "danger",
        icon: "delete",
        message: t.confirmDeleteSingleRole || "Are you sure you want to delete this role?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await apiClient.delete(endpoints.role.delete(id));

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteRoleSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};

export const deleteMultiRoles = async (ids, refresh, alert) => {
    const t = c();

    if (!ids || ids.length === 0) {
        await appAlert(alert, {
            variant: "info",
            title: t.noSelectionTitle || "No selection",
            message: t.noRolesSelected || "No roles selected.",
        });
        return;
    }

    const confirmed = await appConfirm(alert, {
        title: t.deleteRolesTitle || "Delete Roles",
        icon: "delete",
        message: t.confirmDeleteMultipleRoles || "Are you sure you want to delete these roles?",
        confirmText: t.delete || "Delete",
        cancelText: t.cancel || "Cancel",
    });
    if (!confirmed) return;

    try {
        await apiClient.post(endpoints.role.deleteMany(), ids, options);

        await appAlert(alert, {
            variant: "info",
            title: t.deleted || "Deleted",
            message: t.deleteRolesSuccess,
        });

        refresh?.();
    } catch (err) {
        await appAlert(alert, {
            variant: "error",
            title: t.failed || "Failed",
            message: getErrorMessage(err),
        });
    }
};
